import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spin } from "antd";
import {
  CarryOutOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  HistoryOutlined,
  IdcardOutlined,
  OrderedListOutlined,
  PushpinOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { GLOBAL_SEARCH, SIDE_NAV } from "../config/lang";
import { getAbortable } from "../util/action";
import { fill, translate } from "../util/util";

/* Mirrors MIN_QUERY on the endpoint — below this it returns nothing, so don't ask. Three, not
   two, and it is an index constraint rather than a nicety: a two-character needle yields no
   trigram, so `LIKE '%ab%'` cannot use the search indexes at all and degrades to a full scan.
   The two constants move together. */
const MIN_QUERY = 3;
const DEBOUNCE_MS = 250;
/** Bounded so a long session can't grow the cache without limit. */
const CACHE_MAX = 40;
const RECENT_KEY = "picsell.recentSearches";
const RECENT_MAX = 5;

/**
 * Result groups, in display order. Labels reuse the nav's nouns rather than translating six
 * words twice. Each row's icon and colour match the design's per-entity tile — the tint lives in
 * shell.scss as `--tone` per `.shell-search__row--<key>`.
 *
 * `path` builds the detail route; every one of these exists in router.js — except `document`,
 * which has only a list route, so those rows carry a file `url` from the API and open it instead.
 *
 * `list` is where "see all" goes, with the term handed over as `?q=` — the param every redesigned
 * list page reads into its search box. `visit` has none: its filter card has no free-text search
 * (and `VisitWebFilter` has no `search` param to back one), so that group narrows the scope here
 * instead.
 */
const GROUPS = [
  { key: "point", label: "pt_points", Icon: PushpinOutlined, path: id => `/points/${id}`, list: "/points" },
  { key: "merchandiser", label: "merchandiser", Icon: IdcardOutlined, path: id => `/merchandiser/${id}`, list: "/merchandiser" },
  { key: "visit", label: "visit", Icon: EnvironmentOutlined, path: id => `/visit/${id}` },
  { key: "survey", label: "pt_surveys", Icon: OrderedListOutlined, path: id => `/surveys/${id}`, list: "/surveys" },
  { key: "task", label: "tasks", Icon: CarryOutOutlined, path: id => `/task/${id}`, list: "/task" },
  // Documents have only a list route, so these rows carry a file `url` and open it instead.
  { key: "document", label: "documents", Icon: FileTextOutlined, path: () => "/document", list: "/document" },
];

const GROUP_BY_KEY = Object.fromEntries(GROUPS.map(g => [g.key, g]));

const readRecents = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY));
    return Array.isArray(raw) ? raw.filter(x => typeof x === "string").slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
};

/** Wraps the matched span so the user can see *why* a row matched. */
function Highlight({ text, term }) {
  const value = text || "";
  const at = value.toLowerCase().indexOf(term.toLowerCase());
  if (!term || at < 0) return <>{value}</>;
  return (
    <>
      {value.slice(0, at)}
      <mark>{value.slice(at, at + term.length)}</mark>
      {value.slice(at + term.length)}
    </>
  );
}

export default function AppSearch() {
  const me = useSelector(state => state.me);
  const lang = me?.language;
  const history = useHistory();

  const [term, setTerm] = useState("");
  const [scope, setScope] = useState("all");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState({});
  const [cursor, setCursor] = useState(-1);
  const [recents, setRecents] = useState(readRecents);

  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  /** query string -> groups. Re-typing a query already seen costs no request. */
  const cacheRef = useRef(new Map());

  const t = useCallback(key => translate(GLOBAL_SEARCH, key, lang), [lang]);
  const groupLabel = useCallback(key => translate(SIDE_NAV, GROUP_BY_KEY[key].label, lang), [lang]);

  const query = term.trim();
  /* Recents belong to an EMPTY box only. Gating them on `< MIN_QUERY` instead meant that anyone
   with search history typed two characters, got a list of unrelated past searches, and was never
   told the query was below the floor — the one thing they needed to know at that moment. */
  const showRecents = query.length === 0 && recents.length > 0;

  /* One flat list of everything selectable, so ↑/↓ walk results across group boundaries — and
     across the recent-searches list when that is what is on screen. */
  const rows = useMemo(() => {
    if (showRecents) return recents.map(r => ({ recent: r }));
    const out = [];
    for (const g of GROUPS) {
      const hit = groups[g.key];
      if (!hit) continue;
      for (const r of hit.results) out.push({ ...r, type: g.key });
    }
    return out;
  }, [groups, recents, showRecents]);

  const reset = useCallback(() => {
    setGroups({});
    setCursor(-1);
    setLoading(false);
  }, []);

  /* Debounced, cancellable fetch. Every keystroke aborts the request it supersedes, so a fast
     typist leaves one in-flight request rather than one per character — and a slow response for
     "at" can never overwrite the results for "atb". */
  useEffect(() => {
    clearTimeout(timerRef.current);
    abortRef.current?.abort();

    if (query.length < MIN_QUERY) {
      reset();
      return;
    }

    const cacheKey = `${scope}::${query}`;
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setGroups(cached);
      setCursor(-1);
      setLoading(false);
      return;
    }

    setLoading(true);
    timerRef.current = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;
      const url = `search/?q=${encodeURIComponent(query)}${scope === "all" ? "" : `&scope=${scope}`}`;

      getAbortable(url, controller.signal)
        .then(data => {
          if (controller.signal.aborted || !data) return;
          const next = data.groups || {};
          if (cacheRef.current.size >= CACHE_MAX) {
            cacheRef.current.delete(cacheRef.current.keys().next().value);
          }
          cacheRef.current.set(cacheKey, next);
          setGroups(next);
          setCursor(-1);
        })
        .catch(err => {
          /* An aborted request is the expected outcome of typing, not a failure. */
          if (err?.name !== "AbortError") setGroups({});
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timerRef.current);
  }, [query, scope, reset]);

  /* Abort whatever is in flight when the bar unmounts. */
  useEffect(() => () => abortRef.current?.abort(), []);

  /* "/" focuses search from anywhere, unless the user is already typing somewhere. */
  useEffect(() => {
    const onKey = e => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      if (typing) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  /* Click outside closes the panel. */
  useEffect(() => {
    if (!open) return;
    const onDown = e => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const remember = useCallback(value => {
    const next = [value, ...readRecents().filter(r => r !== value)].slice(0, RECENT_MAX);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
      /* private mode — recents just won't survive a reload */
    }
    setRecents(next);
  }, []);

  const go = useCallback(
    row => {
      /* Opening a result is the signal that this query was the useful one. */
      if (query) remember(query);
      setOpen(false);
      setTerm("");
      /* A document has no detail page; the row carries its file URL, so open that. noopener so
         the new context never gets a handle back on this window. */
      if (row.url) {
        window.open(row.url, "_blank", "noopener,noreferrer");
        return;
      }
      history.push(GROUP_BY_KEY[row.type].path(row.id));
    },
    [history, query, remember]
  );

  /* "See all" hands the term to the category's own list page as `?q=`, where the full result set
     lives with its filters, sorting and paging. Two cases fall back to narrowing the scope in the
     panel, which is all this used to do: a group with no list search (`visit`), and standing on the
     destination already — those pages read the URL once, on mount, so a push would not reach them. */
  const seeAll = useCallback(
    g => {
      if (!g.list || history.location.pathname === g.list) {
        setScope(g.key);
        inputRef.current?.focus();
        return;
      }
      if (query) remember(query);
      setOpen(false);
      setTerm("");
      history.push(`${g.list}?q=${encodeURIComponent(query)}`);
    },
    [history, query, remember]
  );

  const activate = useCallback(
    row => {
      if (row.recent !== undefined) {
        setTerm(row.recent);
        inputRef.current?.focus();
        return;
      }
      go(row);
    },
    [go]
  );

  const onKeyDown = e => {
    if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (!rows.length) return;
      e.preventDefault();
      setCursor(c => {
        const step = e.key === "ArrowDown" ? 1 : -1;
        return (c + step + rows.length) % rows.length;
      });
      setOpen(true);
      return;
    }
    if (e.key === "Enter" && cursor >= 0 && rows[cursor]) {
      e.preventDefault();
      activate(rows[cursor]);
    }
  };

  /* Counts come free with the results — the endpoint already fetched LIMIT+1 per category, so
     "5+" is known without a COUNT query. */
  const scopeCount = key => {
    const hit = groups[key];
    if (!hit) return null;
    return `${hit.results.length}${hit.has_more ? "+" : ""}`;
  };

  const hasResults = !showRecents && rows.length > 0;
  let flatIndex = -1;

  return (
    <div className={`shell-search${term ? " has-query" : ""}`} ref={wrapRef}>
      <div className="shell-search__box">
        <SearchOutlined className="shell-search__icon" />
        <input
          ref={inputRef}
          id="global-search"
          name="global-search"
          type="text"
          value={term}
          onChange={e => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={t("search_placeholder")}
          aria-label={t("search_label")}
          role="combobox"
          aria-expanded={open}
          aria-controls="shell-search-panel"
          aria-autocomplete="list"
          autoComplete="off"
        />
        {loading ? <Spin size="small" /> : null}
        {term ? (
          <button
            className="shell-search__clear"
            type="button"
            aria-label={t("search_clear")}
            onClick={() => {
              setTerm("");
              inputRef.current?.focus();
            }}>
            <CloseOutlined />
          </button>
        ) : (
          <span className="shell-search__kbd" aria-hidden="true">
            /
          </span>
        )}
      </div>

      {open ? (
        <div className="shell-search__panel" id="shell-search-panel">
          <div className="shell-search__scopes">
            {[{ key: "all", label: t("search_all") }, ...GROUPS.map(g => ({ key: g.key, label: groupLabel(g.key) }))].map(s => {
              const n = s.key === "all" ? null : scopeCount(s.key);
              return (
                <button
                  key={s.key}
                  className="shell-search__scope"
                  type="button"
                  aria-pressed={scope === s.key}
                  onClick={() => {
                    setScope(s.key);
                    inputRef.current?.focus();
                  }}>
                  {s.label}
                  {n ? <span className="shell-search__n">{n}</span> : null}
                </button>
              );
            })}
          </div>

          <div className="shell-search__results" role="listbox" aria-label={t("search_label")}>
            {showRecents ? (
              <>
                <div className="shell-search__glabel">{t("recent_searches")}</div>
                {recents.map((r, i) => {
                  flatIndex += 1;
                  const active = flatIndex === cursor;
                  return (
                    <button
                      key={r}
                      className={`shell-search__recent${active ? " is-cursor" : ""}`}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onMouseEnter={() => setCursor(i)}
                      onClick={() => activate({ recent: r })}>
                      <HistoryOutlined />
                      <span>{r}</span>
                    </button>
                  );
                })}
              </>
            ) : query.length < MIN_QUERY ? (
              <p className="shell-search__empty">{fill(t("search_hint"), { n: MIN_QUERY })}</p>
            ) : !hasResults ? (
              loading ? null : (
                <p className="shell-search__empty">
                  {t("search_no_results")} “{query}”
                </p>
              )
            ) : (
              GROUPS.map(g => {
                const hit = groups[g.key];
                if (!hit) return null;
                return (
                  <div key={g.key}>
                    <div className="shell-search__glabel">
                      {groupLabel(g.key)} · {hit.results.length}
                      {hit.has_more ? "+" : ""}
                      {/* Only some categories are time-windowed, so only they say so — a note in
                          the panel footer would claim it applied to all of them. */}
                      {hit.recent_days ? <span className="shell-search__window">{fill(t("window_days"), { n: hit.recent_days })}</span> : null}
                    </div>
                    {hit.results.map(r => {
                      flatIndex += 1;
                      const active = flatIndex === cursor;
                      const idx = flatIndex;
                      return (
                        <button
                          key={`${g.key}-${r.id}`}
                          className={`shell-search__row shell-search__row--${g.key}${active ? " is-cursor" : ""}`}
                          type="button"
                          role="option"
                          aria-selected={active}
                          onMouseEnter={() => setCursor(idx)}
                          onClick={() => activate({ ...r, type: g.key })}>
                          <span className="shell-search__row-icon">
                            <g.Icon />
                          </span>
                          <span className="shell-search__row-body">
                            <span className="shell-search__row-title">
                              <Highlight text={r.title} term={query} />
                            </span>
                            {r.meta ? (
                              <span className="shell-search__row-meta">
                                <Highlight text={r.meta} term={query} />
                              </span>
                            ) : null}
                          </span>
                        </button>
                      );
                    })}
                    {/* The endpoint caps every category at 5, so "see all" is how you reach the
                        rest — on the category's list page, with the term prefilled. */}
                    {hit.has_more && (g.list || scope === "all") ? (
                      <button className="shell-search__more" type="button" onClick={() => seeAll(g)}>
                        {t("see_all")} →
                      </button>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>

          {/* Keyboard affordances, as in the design. Wholly decorative — the shortcuts work
              regardless — so the whole strip is `aria-hidden`. The window note used to live here
              and needed an `aria-hidden="false"` island to escape it; it is in the group headers
              now, where it is not hidden in the first place. */}
          <div className="shell-search__foot" aria-hidden="true">
            <span>
              <span className="shell-search__kbd">↑</span>
              <span className="shell-search__kbd">↓</span>
              {t("kb_navigate")}
            </span>
            <span>
              <span className="shell-search__kbd">↵</span>
              {t("kb_open")}
            </span>
            <span>
              <span className="shell-search__kbd">esc</span>
              {t("kb_close")}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
